import React, { useEffect, memo, useCallback, useMemo } from 'react';
import { observer } from 'mobx-react';
import { Col, Form, Row } from 'react-bootstrap';
import InputMask from 'react-input-mask';
import { FieldLabel, IFieldLabelProps, FieldError } from '../../common';
import { useField, useFormikContext } from 'formik';

import { IField } from '../../interface';
import { FieldValidator } from 'formik';

export const PhoneField = observer(
  ({
    className,
    mode = 'vertical',
    name = '',
    label,
    value = '',
    onChange,
    validate,
    disabled,
    ...rest
  }: IField<string> & {
    mode?: 'vertical' | 'horizontal';
    className?: string;
    label?: string;
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
    validate?: FieldValidator;
  }) => {
    const isHorizontal = mode === 'horizontal';
    const fieldProps = isHorizontal ? { as: Row } : {};
    const labelProps: IFieldLabelProps = isHorizontal ? { column: { mode: 'sm', size: 3 } } : {};
    const [inputProps, { error }, { setValue, setTouched }] = useField({ name, value, validate });
    const { validateField } = useFormikContext();

    const mask = useMemo(() => getMask(inputProps.value), [inputProps.value]);

    const changeHandler = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        rewriteValue(e);

        if (onChange) {
          onChange(e);
        }

        setValue(e.target.value);
        setTouched(true);
      },
      [onChange, setValue, setTouched],
    );

    useEffect(() => {
      if (validate) {
        validateField(inputProps.name);
      }
    }, [value, inputProps.name, validate, validateField]);

    return (
      <Form.Group id={name} className={className} {...fieldProps}>
        <FieldLabel label={label} {...labelProps} />
        <Wrap isHorizontal={isHorizontal}>
          <InputMask mask={mask} type="tel" value={inputProps.value || ''} onChange={changeHandler} disabled={disabled}>
            <Form.Control {...rest} isInvalid={!!error} />
          </InputMask>
        </Wrap>
        <FieldError error={error || ''} />
      </Form.Group>
    );
  },
);

const Wrap = memo<{
  children: React.ReactNode;
  isHorizontal?: boolean;
  // eslint-disable-next-line react/jsx-no-useless-fragment
}>(({ children, isHorizontal }) => (isHorizontal ? <Col sm={9}>{children}</Col> : <>{children}</>));

Wrap.displayName = 'Wrap';

const getMask = (phone: string) =>
  PHONE_MASKS_MAP.find(([prefix]) =>
    typeof prefix == 'string' ? phone.startsWith(prefix) : prefix.test(phone),
  )?.[1] || '';

const PHONE_MASKS_MAP: ReadonlyArray<readonly [RegExp | string, string]> = [
  // Комментарий для форматирования
  ['+7', '+9 (999) 999-99-99'],
];

const rewriteValue = (e: React.ChangeEvent<HTMLInputElement>) =>
  REWRITE_MAP.find(([predicate]) =>
    typeof predicate == 'string' ? e.target.value.startsWith(predicate) : predicate(e),
  )?.[1](e);

const REWRITE_MAP: ReadonlyArray<
  readonly [
    string | ((e: React.ChangeEvent<HTMLInputElement>) => boolean),
    (e: React.ChangeEvent<HTMLInputElement>) => unknown,
  ]
> = [
  ['8', (e) => (e.target.value = `+7${e.target.value.slice(1)}`)],
  ['9', (e) => (e.target.value = `+7${e.target.value}`)],

  // При использовании react-input-mask нельзя просто взять и очистить поле ввода полностью:
  // например, при выделении всего поля ввода и очистке значения, в поле остаётся пустая маска,
  // что нам не подходит. Поэтому создано следующее правило:
  [
    // Проверяет значение на соответствие пустой маске. Например, `+_ (___) ___-__-__`
    (e) => !!e.target.value && e.target.value !== '+' && !HAS_NUMBER_REG_EXP.test(e.target.value),
    (e) => (e.target.value = ''),
  ],
];

// eslint-disable-next-line require-unicode-regexp
const HAS_NUMBER_REG_EXP = /[0-9]/;
